import React, { useEffect } from 'react';
import ResellerDashboard from '../ResellerDashboard';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import { Typography, Button, Modal, Divider, Form, Input, Slider, Row, Col } from 'antd';
import SupportCard from '../ResellerContactSupport/SupportCard';
import { Calculator, Crown } from 'lucide-react';
import ResellerLevelsModal from '../ResellerReceptionist/ResellerLevelModal';
import { DollarCircleFilled } from '@ant-design/icons';
import { getResellerData } from '@/store/reseller/selector';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import Label from '@/components/Label/Label';
import {
	CALCULATOR_DEFAULTS,
	PROFIT_EXAMPLES,
	RESELLER_LEVELS,
	SLIDER_MILESTONES,
} from '../../../constants/resellerLevels';
import {
	getResellerLevel,
	calculateProfit,
	formatCurrency,
} from '../../../utils/resellerLevelUtils';

const { Title, Text } = Typography;

const ResellerAdvanceSettings: React.FC = () => {
	const resellerData = useAppSelector(getResellerData);
	const [isCalculatorModalVisible, setIsCalculatorModalVisible] = React.useState<boolean>(false);
	const [sellingPricePerSeat, setSellingPricePerSeat] = React.useState<number>(
		CALCULATOR_DEFAULTS.DEFAULT_SEAT_PRICE,
	);
	const [numberOfSeats, setNumberOfSeats] = React.useState<number>(
		CALCULATOR_DEFAULTS.DEFAULT_SEATS,
	);
	const [currentResellerLevel, setCurrentResellerLevel] = React.useState<string>(
		RESELLER_LEVELS[0].level,
	);
	const [isLevelModalVisible, setIsLevelModalVisible] = React.useState<boolean>(false);

	const openResellerLevelModal = () => {
		setIsLevelModalVisible(true);
	};

	const closeResellerLevelModal = () => {
		setIsLevelModalVisible(false);
	};

	const openEarningsCalculatorModal = () => {
		setIsCalculatorModalVisible(true);
	};

	const closeEarningsCalculatorModal = () => {
		setIsCalculatorModalVisible(false);
	};

	useEffect(() => {
		if (resellerData?.configuredInboundAgents) {
			const totalConfiguredClients =
				resellerData?.configuredInboundAgents + resellerData?.unConfiguredInboundAgents || 0;
			const { currentLevel } = getResellerLevel(totalConfiguredClients);
			setCurrentResellerLevel(currentLevel);
		}
	}, [resellerData]);

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.ADVANCED_SETTINGS}>
			<div className="p-8">
				<div className="flex items-center justify-between mb-6">
					<div>
						<Title level={2} className="!mb-2">
							Advanced Settings
						</Title>
						<Text type="secondary" className="block max-w-2xl">
							Configure your advanced business settings
						</Text>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<SupportCard
						icon={<Calculator size={32} color="#4096ff" />}
						title="Earnings Calculator"
						description="Calculate your earnings based on your pricing and call volume"
						buttonColor="blue"
					>
						<Button
							type="primary"
							size="large"
							icon={<Calculator size={18} />}
							onClick={openEarningsCalculatorModal}
						>
							Calculate Earnings
						</Button>
					</SupportCard>
					<SupportCard
						icon={<Crown size={32} color="#722ed1" />}
						title="Reseller Progress"
						description="View your reseller progress and level"
						buttonColor="purple"
					>
						<Button
							type="primary"
							size="large"
							icon={<Crown size={18} />}
							onClick={openResellerLevelModal}
						>
							View Reseller Progress
						</Button>
					</SupportCard>
				</div>

				<Modal
					open={isCalculatorModalVisible}
					onCancel={closeEarningsCalculatorModal}
					footer={null}
					width={600}
				>
					<div className="text-center">
						<h4 className="mb-0 font-bold text-xl">Your Profit Potential</h4>
						<div className="text-green-600">
							<h2 className="mt-2 mb-2 text-green-600 text-3xl font-bold">
								{formatCurrency(calculateProfit(numberOfSeats, sellingPricePerSeat).annualProfit)}{' '}
								per year
							</h2>
							<Text>
								Selling {numberOfSeats} seats at ${sellingPricePerSeat}/seat.
							</Text>
						</div>
					</div>
					<Form layout="vertical">
						<Form.Item
							name="selling-price"
							label={<Label text="Your Selling Price (per seat/month)" />}
						>
							<Input
								prefix={<DollarCircleFilled />}
								onChange={(e) => setSellingPricePerSeat(Number(e.target.value) || 0)}
								size="large"
								type="number"
								min={CALCULATOR_DEFAULTS.MIN_SEAT_PRICE}
								defaultValue={sellingPricePerSeat}
								placeholder="e.g. 275"
								className="py-1.5"
							/>
						</Form.Item>

						<Text className="mt-[-.5rem] block">
							Your cost: ${CALCULATOR_DEFAULTS.MIN_SEAT_PRICE}/seat | Profit margin: $
							{calculateProfit(numberOfSeats, sellingPricePerSeat).profitPerSeat}/seat
						</Text>
						<Form.Item name={'seats'} label={<Label text={'Number of Seats to Sell'} />}>
							<Slider
								defaultValue={numberOfSeats}
								min={CALCULATOR_DEFAULTS.MIN_SEATS}
								max={CALCULATOR_DEFAULTS.MAX_SEATS}
								onChange={(value) => setNumberOfSeats(value as number)}
							/>
						</Form.Item>

						<div className="flex justify-between mt-[-1rem] ml-[.4rem] mr-[.4rem]">
							{SLIDER_MILESTONES.map((milestone) => (
								<div key={milestone} className="flex flex-col items-center">
									<div className="w-2 h-2 bg-blue-500 rounded-full mb-1" />
									<Text className="text-gray-600 text-xs font-medium">{milestone}</Text>
								</div>
							))}
						</div>
					</Form>

					<Row className="mt-5" gutter={10}>
						<Col span={12}>
							<div className="rounded-lg p-3 bg-blue-50">
								<div className="text-center">
									<Text>Monthly Profit</Text>
									<h2 className="mt-2 mb-0 text-3xl font-bold text-blue-600">
										{formatCurrency(
											calculateProfit(numberOfSeats, sellingPricePerSeat).monthlyProfit,
										)}
									</h2>
								</div>
							</div>
						</Col>
						<Col span={12}>
							<div className="rounded-lg p-3 bg-green-50">
								<div className="text-center">
									<Text>Annual Profit</Text>
									<h2 className="mt-2 mb-0 text-3xl font-bold text-green-600">
										{formatCurrency(
											calculateProfit(numberOfSeats, sellingPricePerSeat).annualProfit,
										)}
									</h2>
								</div>
							</div>
						</Col>
					</Row>
					<Divider />
					<p>Quick Profit Examples:</p>
					<div className="grid grid-cols-2 gap-x-3">
						{PROFIT_EXAMPLES.map(({ seats, label }) => (
							<div key={seats} className="flex justify-between p-1 rounded-lg">
								<p className="my-0 text-sm">
									{label} at ${sellingPricePerSeat}
								</p>
								<p className="my-0 text-sm font-semibold">
									{formatCurrency(calculateProfit(seats, sellingPricePerSeat).annualProfit)}/year
								</p>
							</div>
						))}
					</div>
				</Modal>

				<ResellerLevelsModal
					showLevel={isLevelModalVisible}
					closeLevelModal={closeResellerLevelModal}
					resellerLevels={RESELLER_LEVELS}
					currentLevel={currentResellerLevel}
				/>
			</div>
		</ResellerDashboard>
	);
};

export default ResellerAdvanceSettings;
